import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Users, Download, Search, Filter, ChevronLeft, ChevronRight, 
  Star, Phone, Mail, MessageCircle, Trash2, Edit2, FileText, 
  BarChart3, Globe, Building2, X, Plus, Copy, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const token = () => sessionStorage.getItem('adminToken');

const api = async (url, options = {}) => {
  const res = await fetch(`${BACKEND_URL}${url}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}`, ...options.headers }
  });
  if (!res.ok) throw new Error('Request failed');
  return res.json();
};

const ScoreBadge = ({ score }) => {
  let color = 'bg-gray-100 text-gray-600';
  if (score >= 60) color = 'bg-green-100 text-green-800';
  else if (score >= 30) color = 'bg-yellow-100 text-yellow-800';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>{score}</span>;
};

const CompletionBar = ({ contact }) => {
  const fields = ['full_name', 'email', 'whatsapp', 'country', 'company', 'job_title', 'industry', 'interest_category'];
  const filled = fields.filter(f => contact[f]).length;
  const pct = Math.round((filled / fields.length) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 bg-gray-200 rounded-full h-1.5"><div className="bg-blue-500 h-1.5 rounded-full" style={{width: `${pct}%`}} /></div>
      <span className="text-xs text-gray-500">{pct}%</span>
    </div>
  );
};

// ========== ANALYTICS TAB ==========
const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/admin/contacts/analytics')
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"/></div>;
  if (!data) return <p className="text-gray-500 text-center py-10">Failed to load analytics</p>;

  const stats = [
    { label: 'Total Contacts', value: data.totalContacts, icon: Users, color: 'text-blue-600 bg-blue-50' },
    { label: 'Total Downloads', value: data.totalDownloads, icon: Download, color: 'text-green-600 bg-green-50' },
    { label: 'New This Month', value: data.newThisMonth, icon: Star, color: 'text-amber-600 bg-amber-50' },
    { label: 'Returning Visitors', value: data.returningVisitors, icon: Users, color: 'text-purple-600 bg-purple-50' },
    { label: 'Avg. Profile', value: `${data.avgProfileCompletion}%`, icon: BarChart3, color: 'text-teal-600 bg-teal-50' },
    { label: 'Most Downloaded', value: data.mostDownloaded, icon: FileText, color: 'text-red-600 bg-red-50', small: true },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map(s => (
          <Card key={s.label} className="border">
            <CardContent className="p-4 text-center">
              <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center mb-2 ${s.color}`}>
                <s.icon size={18} />
              </div>
              <p className={`font-bold ${s.small ? 'text-sm' : 'text-xl'} text-gray-900`}>{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-sm">Top Countries</CardTitle></CardHeader>
          <CardContent>
            {data.topCountries.length === 0 ? <p className="text-sm text-gray-400">No data yet</p> : (
              <div className="space-y-2">
                {data.topCountries.map(c => (
                  <div key={c.country} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 flex items-center gap-2"><Globe size={12} className="text-gray-400" />{c.country}</span>
                    <span className="text-sm font-semibold text-gray-900">{c.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm">Contacts by Interest</CardTitle></CardHeader>
          <CardContent>
            {data.interests.length === 0 ? <p className="text-sm text-gray-400">No data yet</p> : (
              <div className="space-y-2">
                {data.interests.map(i => (
                  <div key={i.interest} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{i.interest}</span>
                    <span className="text-sm font-semibold text-gray-900">{i.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ========== CONTACT DETAIL PANEL ==========
const ContactDetail = ({ contactId, onClose, onUpdate }) => {
  const [contact, setContact] = useState(null);
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (contactId) {
      api(`/api/admin/contacts/${contactId}`).then(c => { setContact(c); setEditData(c); }).catch(() => {});
    }
  }, [contactId]);

  const handleCopy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied!`); };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    try {
      await api(`/api/admin/contacts/${contactId}/notes`, { method: 'POST', body: JSON.stringify({ note: note.trim() }) });
      toast.success('Note added');
      setNote('');
      const updated = await api(`/api/admin/contacts/${contactId}`);
      setContact(updated);
    } catch { toast.error('Failed to add note'); }
  };

  const handleSaveEdit = async () => {
    try {
      const res = await api(`/api/admin/contacts/${contactId}`, { method: 'PATCH', body: JSON.stringify(editData) });
      setContact(res.contact);
      setEditing(false);
      if (onUpdate) onUpdate();
      toast.success('Contact updated');
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this contact permanently?')) return;
    try {
      await api(`/api/admin/contacts/${contactId}`, { method: 'DELETE' });
      toast.success('Contact deleted');
      if (onUpdate) onUpdate();
      onClose();
    } catch { toast.error('Failed to delete'); }
  };

  if (!contact) return <div className="flex justify-center py-10"><div className="animate-spin h-6 w-6 border-3 border-blue-500 border-t-transparent rounded-full"/></div>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="contact-detail-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="font-bold text-lg text-gray-900">{contact.full_name}</h3>
            <p className="text-sm text-gray-500">{contact.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <ScoreBadge score={contact.lead_score || 0} />
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => handleCopy(contact.email, 'Email')} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium transition" data-testid="copy-email-btn"><Copy size={12} /><Mail size={12} /> Copy Email</button>
            <button onClick={() => handleCopy(contact.whatsapp, 'Phone')} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium transition" data-testid="copy-phone-btn"><Copy size={12} /><Phone size={12} /> Copy Phone</button>
            <a href={`https://wa.me/${(contact.whatsapp || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg text-xs font-medium transition" data-testid="open-whatsapp-btn"><MessageCircle size={12} /> WhatsApp</a>
            <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg text-xs font-medium transition" data-testid="open-email-btn"><ExternalLink size={12} /> Email</a>
            <button onClick={() => setEditing(!editing)} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-xs font-medium transition"><Edit2 size={12} /> Edit</button>
            <button onClick={handleDelete} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-xs font-medium transition" data-testid="delete-contact-btn"><Trash2 size={12} /> Delete</button>
          </div>

          {/* Profile Fields */}
          {editing ? (
            <div className="grid grid-cols-2 gap-3 bg-gray-50 p-4 rounded-lg">
              {['full_name', 'email', 'whatsapp', 'company', 'country', 'job_title', 'industry', 'interest_category'].map(f => (
                <div key={f}>
                  <label className="text-xs font-medium text-gray-600 capitalize">{f.replace(/_/g, ' ')}</label>
                  <input value={editData[f] || ''} onChange={e => setEditData({...editData, [f]: e.target.value})}
                    className="w-full px-2 py-1.5 border rounded text-sm mt-0.5" />
                </div>
              ))}
              <div className="col-span-2 flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="sm" onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700 text-white">Save</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Phone / WhatsApp', value: contact.whatsapp, icon: Phone },
                { label: 'Company', value: contact.company, icon: Building2 },
                { label: 'Country', value: contact.country, icon: Globe },
                { label: 'Job Title', value: contact.job_title, icon: Users },
                { label: 'Industry', value: contact.industry, icon: BarChart3 },
                { label: 'Interest', value: contact.interest_category, icon: Star },
                { label: 'Source', value: contact.source },
                { label: 'Downloads', value: contact.download_count || 0 },
                { label: 'Registered', value: contact.first_registered ? new Date(contact.first_registered).toLocaleDateString() : '' },
                { label: 'Last Activity', value: contact.latest_activity ? new Date(contact.latest_activity).toLocaleDateString() : '' },
              ].map(f => (
                <div key={f.label} className="text-sm">
                  <span className="text-gray-500 text-xs">{f.label}</span>
                  <p className="font-medium text-gray-900">{f.value || <span className="text-gray-300">—</span>}</p>
                </div>
              ))}
            </div>
          )}

          {/* Download History */}
          {contact.downloads && contact.downloads.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><Download size={14} /> Download History</h4>
              <div className="space-y-1">
                {contact.downloads.map((d, i) => (
                  <div key={`dl-${d.downloaded_at}-${i}`} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-xs">
                    <span className="font-medium text-gray-800">{d.resource}</span>
                    <span className="text-gray-500">{new Date(d.downloaded_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><FileText size={14} /> Internal Notes</h4>
            {(contact.notes || []).map((n, i) => (
              <div key={`note-${n.added_at}-${i}`} className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-2 text-xs">
                <p className="text-gray-800">{n.text}</p>
                <p className="text-gray-400 mt-1">{n.by} — {new Date(n.added_at).toLocaleString()}</p>
              </div>
            ))}
            <div className="flex gap-2">
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note..."
                className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="add-note-input"
                onKeyDown={e => e.key === 'Enter' && handleAddNote()} />
              <Button size="sm" onClick={handleAddNote} className="bg-gray-800 text-white"><Plus size={14} /></Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== MAIN COMPONENT ==========
export const AdminContacts = () => {
  const [tab, setTab] = useState('contacts'); // contacts | analytics
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ country: '', interest: '', min_score: 0 });
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const fetchContacts = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: p, limit: 25, search,
        country: filters.country, interest: filters.interest, min_score: filters.min_score
      });
      const data = await api(`/api/admin/contacts?${params}`);
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(p);
    } catch { toast.error('Failed to load contacts'); }
    setLoading(false);
  }, [search, filters]);

  useEffect(() => { fetchContacts(1); }, [fetchContacts]);

  const handleSearchChange = (val) => {
    setSearch(val);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => fetchContacts(1), 400));
  };

  const handleExport = () => {
    const params = new URLSearchParams({ search, country: filters.country, interest: filters.interest, min_score: filters.min_score });
    window.open(`${BACKEND_URL}/api/admin/contacts/export?${params}&_t=${Date.now()}`, '_blank');
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="admin-contacts-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contacts & Leads</h1>
            <p className="text-sm text-gray-500">{total} total contacts</p>
          </div>
          <div className="flex gap-2">
            <Button variant={tab === 'contacts' ? 'default' : 'outline'} size="sm" onClick={() => setTab('contacts')} data-testid="tab-contacts">
              <Users size={14} className="mr-1" /> Contacts
            </Button>
            <Button variant={tab === 'analytics' ? 'default' : 'outline'} size="sm" onClick={() => setTab('analytics')} data-testid="tab-analytics">
              <BarChart3 size={14} className="mr-1" /> Analytics
            </Button>
          </div>
        </div>

        {tab === 'analytics' ? <AnalyticsTab /> : (
          <>
            {/* Search & Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={search} onChange={e => handleSearchChange(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" placeholder="Search name, email, company..."
                  data-testid="contacts-search" />
              </div>
              <input value={filters.country} onChange={e => setFilters({...filters, country: e.target.value})}
                className="px-3 py-2 border rounded-lg text-sm w-36" placeholder="Country" data-testid="filter-country" />
              <input value={filters.interest} onChange={e => setFilters({...filters, interest: e.target.value})}
                className="px-3 py-2 border rounded-lg text-sm w-36" placeholder="Interest" data-testid="filter-interest" />
              <select value={filters.min_score} onChange={e => setFilters({...filters, min_score: parseInt(e.target.value)})}
                className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="filter-score">
                <option value="0">All Scores</option>
                <option value="20">Score 20+</option>
                <option value="40">Score 40+</option>
                <option value="60">Score 60+</option>
              </select>
              <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-csv-btn">
                <Download size={14} className="mr-1" /> Export CSV
              </Button>
            </div>

            {/* Contacts Table */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-10"><div className="animate-spin h-6 w-6 border-3 border-blue-500 border-t-transparent rounded-full" /></div>
                ) : contacts.length === 0 ? (
                  <div className="text-center py-10">
                    <Users size={40} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">No contacts found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Company</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Country</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">Interest</th>
                          <th className="text-center px-4 py-3 font-medium text-gray-600">Score</th>
                          <th className="text-center px-4 py-3 font-medium text-gray-600 hidden md:table-cell">Profile</th>
                          <th className="text-center px-4 py-3 font-medium text-gray-600">DL</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {contacts.map(c => (
                          <tr key={c.contact_id} className="hover:bg-gray-50 cursor-pointer transition"
                            onClick={() => setSelectedContact(c.contact_id)} data-testid={`contact-row-${c.contact_id}`}>
                            <td className="px-4 py-3 font-medium text-gray-900">{c.full_name}</td>
                            <td className="px-4 py-3 text-gray-600">{c.email}</td>
                            <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{c.company || '—'}</td>
                            <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{c.country || '—'}</td>
                            <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{c.interest_category || '—'}</td>
                            <td className="px-4 py-3 text-center"><ScoreBadge score={c.lead_score || 0} /></td>
                            <td className="px-4 py-3 hidden md:table-cell"><CompletionBar contact={c} /></td>
                            <td className="px-4 py-3 text-center text-gray-600">{c.download_count || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">Page {page} of {pages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchContacts(page - 1)}>
                    <ChevronLeft size={14} /> Prev
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => fetchContacts(page + 1)}>
                    Next <ChevronRight size={14} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Contact Detail Panel */}
        {selectedContact && (
          <ContactDetail contactId={selectedContact} onClose={() => setSelectedContact(null)} onUpdate={() => fetchContacts(page)} />
        )}
      </div>
    </AdminLayout>
  );
};
